import React from 'react'
import { FaArrowRight, FaExternalLinkAlt, FaFilePdf, FaTimes } from 'react-icons/fa'
import { useSelector } from 'react-redux'
import { KTSVG } from '../../../_metronic/helpers'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import DateComp from '../../../components/dateComponent/DateComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import ExpenseStatusComp from '../../../components/StatusComp/ExpenseStatusComp'
import { constraintConfig } from '../../../constraintConfig'
import { selectAuth } from '../../../features/authSlice'
import labelKey from '../../localization/label.json'

type Props = {
  selectedDraftData: any
  title?: string
  /** Optional accent: from (yellow), into (blue), preview (green merged result) */
  variant?: 'from' | 'into' | 'preview'
  /** Clear/close the merge-into selection panel */
  onClose?: () => void
  /** Match labels vs the other line item (date / amount / best). */
  matchHints?: {
    dateMatch?: boolean
    amountMatch?: boolean
    isBestMatch?: boolean
  }
}

const renderSource = (recordSourceId?: number) => {
  if (recordSourceId === constraintConfig.recordSourceId.WebApp) {
    return <KTSVG path="/media/icons/figmaIcons/Web.svg" className="svg-icon-1" />
  }
  if (recordSourceId === constraintConfig.recordSourceId.Mobile) {
    return <KTSVG path="/media/icons/figmaIcons/Mobile.svg" className="svg-icon-1" />
  }
  if (recordSourceId === constraintConfig.recordSourceId.BankFeed) {
    return <KTSVG path="/media/icons/figmaIcons/Bank.svg" className="svg-icon-1" />
  }
  if (recordSourceId === constraintConfig.recordSourceId.GorillaExpense) {
    return <KTSVG path="/media/icons/figmaIcons/Gorilla.svg" className="svg-icon-1" />
  }
  return <span className="text-muted">—</span>
}

const isPdfFile = (filePath: string | undefined) => {
  if (!filePath) return false
  return filePath.toLowerCase().includes('.pdf')
}

export const isMergeAdminViewer = (roleID?: number | null) =>
  roleID === constraintConfig.roleID.role1 ||
  roleID === constraintConfig.roleID.role2 ||
  roleID === constraintConfig.roleID.roleIDAdmin ||
  roleID === constraintConfig.roleID.roleIDPortalSupport

/** Org Admin | Admin | Support Team — Line Item ID & SS ReceiptID on merge UI. */
export const canViewInvoiceDataDraftId = (roleID?: number | null) =>
  roleID === constraintConfig.roleID.role2 ||
  roleID === constraintConfig.roleID.roleIDAdmin ||
  roleID === constraintConfig.roleID.roleIDPortalSupport

/** Same roles — Internal Notes on Merged Result (Preview). */
export const canViewMergeInternalNotes = canViewInvoiceDataDraftId

export const getReceiptNumber = (d: any): string => {
  if (!d) return '—'
  const n =
    d.invoiceNo ||
    d.receiptInvoiceNo ||
    d.invoiceNumber ||
    d.invoiceDataDraft?.invoiceNo ||
    d.ocrInvoiceNo
  const draftId = d.invoiceDataDraftId
  if (n) {
    // If there's also a draft id, include it alongside the receipt number
    if (draftId) return `${String(n)} (Draft #${draftId})`
    return String(n)
  }
  if (draftId) return `Draft #${draftId}`
  if (d.expenseItemId) return `LI #${d.expenseItemId}`
  return '—'
}

/**
 * Return separate receipt and draft values so UI can render them individually.
 */
export const getReceiptParts = (d: any): { receipt: string | null; draftId: number | null } => {
  if (!d) return { receipt: null, draftId: null }
  const receipt =
    d.invoiceNo ||
    d.receiptInvoiceNo ||
    d.invoiceNumber ||
    d.invoiceDataDraft?.invoiceNo ||
    d.ocrInvoiceNo ||
    null
  const draftId = d.invoiceDataDraftId ?? null
  return { receipt: receipt ? String(receipt) : null, draftId }
}

/** Max 20 chars; if longer, show first 7 + ... + last 13. */
export const formatReceiptNumberDisplay = (receiptNo: string | null | undefined): string => {
  const s = receiptNo != null ? String(receiptNo) : ''
  if (!s || s === '—') return '—'
  if (s.length <= 20) return s
  return `${s.slice(0, 7)}...${s.slice(-13)}`
}

export const getMergeAttachmentUrl = (d: any): string => {
  if (!d) return ''
  const candidates = [
    d.attachmentFile,
    d.fileURL,
    d.fileUrl,
    d.expenseItemAttachmentFile,
    d.invoiceDataDraft?.fileURL,
    d.invoiceDataDraft?.attachmentFile,
    d.ocrFileURL,
  ]
  for (const c of candidates) {
    const s = c != null ? String(c).trim() : ''
    if (
      s &&
      s !== 'null' &&
      s !== 'undefined' &&
      s !== 'N/A' &&
      s.toLowerCase() !== 'receipt is not available'
    ) {
      return s
    }
  }
  return ''
}

export const hasMergeAttachment = (d: any): boolean => !!getMergeAttachmentUrl(d)

/** Compact sticky strip: Item Date, Amount, and Receipt # (only when attachment exists). */
export const MergeStickyItemSummary = ({
  item,
  label = 'From',
  visible,
}: {
  item: any
  label?: string
  visible: boolean
}) => {
  if (!item) return null
  const showReceipt = hasMergeAttachment(item)
  const { receipt: stickyReceipt, draftId: stickyDraftId } = getReceiptParts(item)
  return (
    <div
      className={`merge-sticky-item-summary ${
        visible ? 'border-bottom shadow-sm px-3 py-2' : ''
      }`}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        maxHeight: visible ? 120 : 0,
        overflow: 'hidden',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.15s ease',
      }}
      aria-hidden={!visible}
    >
      <div className="d-flex flex-wrap align-items-center gap-3 gap-lg-4">
        <span className="badge badge-light-dark">{label}</span>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10 }}>
            {labelKey.lineItemDate || 'Item Date'}
          </span>
          <span className="fw-bold text-gray-900 fs-7">
            <DateComp formattedDate={item.itemDate} />
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10 }}>
            {labelKey.amount || 'Amount'}
          </span>
          <span className="fw-bold text-gray-900 fs-6">
            <CurrencyComp amount={item.amount} />
          </span>
        </div>
        {showReceipt ? (
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10 }}>
              Receipt #
            </span>
            {stickyReceipt ? (
              <span className="badge badge-light-primary" title={stickyReceipt !== formatReceiptNumberDisplay(stickyReceipt) ? stickyReceipt : undefined}>
                {formatReceiptNumberDisplay(stickyReceipt)}
              </span>
            ) : null}
            {stickyDraftId ? (
              <span className="badge badge-light-primary" title={`Draft #${stickyDraftId}`}>
                {`Draft #${stickyDraftId}`}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export const getMergeAdminMeta = (d: any) => {
  const expense = d?.expense || {}
  const notOnReport = d?.expenseId == null || d?.expenseId === -1 || d?.expenseId === 0
  const trackingId =
    d?.trackingId || expense.trackingId || (notOnReport ? '—' : '—')
  const title =
    d?.expenseTitle || d?.title || expense.title || (notOnReport ? 'Not on report' : '—')
  const reportStatusId = Number(
    d?.expenseStatusId ??
      d?.expenseStatus?.id ??
      expense.expenseStatus?.id ??
      expense.expenseStatusId ??
      0
  )
  const reportStatusRaw =
    d?.expenseStatusTitle ||
    d?.expenseStatus?.title ||
    expense.expenseStatus?.title ||
    ''
  // Show real report status only (never "Not Submitted" placeholder)
  const reportStatus =
    reportStatusRaw && reportStatusRaw !== 'Not Submitted' && (reportStatusId > 0 || !notOnReport)
      ? reportStatusRaw
      : reportStatusId > 0
        ? reportStatusRaw
        : ''
  const reportCategory =
    d?.expenseReportCategoryTitle ||
    d?.expenseReportCategory?.title ||
    expense.expenseReportCategory?.title ||
    expense.expenseReportCategoryTitle ||
    '—'
  const expenseItemId = d?.expenseItemId ?? '—'
  const invoiceDataDraftId =
    d?.invoiceDataDraftId && Number(d.invoiceDataDraftId) > 0 ? d.invoiceDataDraftId : '—'
  const hasExpenseDetails =
    (trackingId && trackingId !== '—') ||
    (title && title !== '—') ||
    (reportStatus && reportStatus !== '—')
  return {
    trackingId,
    title,
    reportStatusId,
    reportStatus,
    reportCategory,
    expenseItemId,
    invoiceDataDraftId,
    hasExpenseDetails,
  }
}

const InfoField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="mb-1">
    <div className="merge-panel-label text-uppercase fw-semibold lh-1 mb-1" style={{ fontSize: 9 }}>
      {label}
    </div>
    <div className="merge-panel-value fw-semibold text-break" style={{ fontSize: 11, lineHeight: 1.2 }}>
      {children}
    </div>
  </div>
)

/** Details + receipt preview for a merge line item. */
const MergeSelectedLineItemHeader = ({
  selectedDraftData,
  title,
  variant = 'from',
  onClose,
  matchHints,
}: Props) => {
  const { roleID } = useSelector(selectAuth)
  const showRestrictedIds = canViewInvoiceDataDraftId(roleID)
  const d = selectedDraftData || {}
  const attachmentFile = getMergeAttachmentUrl(d)
  const hasReceipt = !!attachmentFile
  const isPdf = isPdfFile(attachmentFile)
  const { receipt: receiptNo, draftId } = getReceiptParts(d)
  const receiptNoDisplay = formatReceiptNumberDisplay(receiptNo)
  const adminMeta = getMergeAdminMeta(d)
  const dateMatch = !!matchHints?.dateMatch
  const amountMatch = !!matchHints?.amountMatch
  const isBestMatch = !!matchHints?.isBestMatch
  const isPreview = variant === 'preview'
  const panelVariantClass =
    variant === 'preview' ? 'merge-panel--preview' : variant === 'into' ? 'merge-panel--into' : 'merge-panel--from'

  const openReceiptInNewTab = () => {
    if (!attachmentFile) return
    window.open(attachmentFile, '_blank', 'noopener,noreferrer')
  }

  const defaultTitle =
    variant === 'preview'
      ? 'Merged Result (Preview)'
      : variant === 'into'
        ? 'Selected Line Item (merge into)'
        : 'Selected Line Item (merge from)'

  return (
    <div
      className={`merge-panel ${panelVariantClass} border rounded px-2 px-lg-3 py-2 mb-2 position-relative`}
    >
      <div
        className={`d-flex flex-wrap align-items-center justify-content-between gap-1 mb-1${isPreview ? ' merge-panel-titlebar' : ''}`}
      >
        <div>
          <h5 className="mb-0 fw-bold text-gray-900">
            {title || defaultTitle}
          </h5>
          {isPreview ? (
            <div className="text-muted">
              This is how the merged line item will look after merge.
            </div>
          ) : null}
        </div>
        <div className="d-flex flex-wrap align-items-center gap-1">
          {isBestMatch ? <span className="badge badge-success" style={{ fontSize: 9 }}>Best match</span> : null}
          {isPreview && dateMatch ? (
            <span className="badge badge-light-success" style={{ fontSize: 9 }}>
              Date match
            </span>
          ) : null}
          {isPreview && amountMatch ? (
            <span className="badge badge-light-success" style={{ fontSize: 9 }}>
              Amount match
            </span>
          ) : null}
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10 }}>
              Receipt #
            </span>
            {receiptNo ? (
              <span
                className="badge badge-light-primary"
                title={receiptNo !== formatReceiptNumberDisplay(receiptNo) ? receiptNo : undefined}
              >
                {formatReceiptNumberDisplay(receiptNo)}
              </span>
            ) : null}
            {draftId ? (
              <span className="badge badge-light-primary" title={`Draft #${draftId}`}>
                {`Draft #${draftId}`}
              </span>
            ) : null}
          </div>
          <span
            className={`badge ${hasReceipt ? 'badge-light-success' : 'badge-light-warning'}`}
            style={{ fontSize: 9 }}
          >
            {hasReceipt ? 'Has receipt' : 'No receipt'}
          </span>
          {isPreview && hasReceipt ? (
            <button
              type="button"
              className="btn btn-sm btn-light-primary d-inline-flex align-items-center gap-1 py-1 px-2"
              style={{ fontSize: 10 }}
              title="Open final attached receipt in new tab"
              onClick={openReceiptInNewTab}
            >
              <FaExternalLinkAlt size={10} />
              Open in new tab
            </button>
          ) : null}
          {variant === 'into' && onClose ? (
            <button
              type="button"
              className="btn btn-sm btn-icon btn-light-danger"
              title="Clear selected merge-into item"
              aria-label="Close second selected item"
              onClick={onClose}
            >
              <FaTimes />
            </button>
          ) : null}
        </div>
      </div>


      <div className="row g-2 align-items-stretch">
        <div className={isPreview ? 'col-12 col-lg-10' : 'col-12 col-lg-8'} style={isPreview ? { flex: '0 0 70%', maxWidth: '70%' } : undefined}>
          <div className="row g-1">
      <div className={`row g-1 mb-1 p-1 border rounded merge-panel-card`}>
          <div className="col-3 col-md-2">
            <InfoField label="Tracking ID">{adminMeta.trackingId}</InfoField>
          </div>
          <div className="col-6 col-md-8">
            <InfoField label="Expense Title">{adminMeta.title}</InfoField>
          </div>
          <div className="col-3 col-md-2">
            <InfoField label="Expense Status">
              {adminMeta.reportStatusId > 0 && adminMeta.reportStatus ? (
                <ExpenseStatusComp
                  expenseStatusId={adminMeta.reportStatusId}
                  expenseStatusTitle={adminMeta.reportStatus}
                />
              ) : null}
            </InfoField>
          </div>
        </div>
            <div className="col-6 col-md-4">
              <InfoField label={labelKey.category || 'Category'}>
                {d.expenseCategory?.title || '—'}
              </InfoField>
            </div>
             <div className="col-6 col-md-4">
              <InfoField label={labelKey.lineItemDate || 'Line Item Date'}>
                <span className="d-inline-flex flex-wrap align-items-center gap-1">
                  <DateComp formattedDate={d.itemDate} />
                  {dateMatch ? (
                    <span className={`badge ${isBestMatch && !amountMatch ? 'badge-success' : 'badge-light-success'}`}>
                      {isBestMatch && !amountMatch ? 'Best match' : 'Date match'}
                    </span>
                  ) : null}
                </span>
              </InfoField>
            </div>
            <div className="col-6 col-md-4">
              <InfoField label={labelKey.amount || 'Amount'}>
                <span className="d-inline-flex flex-wrap align-items-center gap-1">
                  <span className="fw-bold fs-6">
                    <CurrencyComp amount={d.amount} />
                  </span>
                  {amountMatch ? (
                    <span className={`badge ${isBestMatch ? 'badge-success' : 'badge-light-success'}`}>
                      {isBestMatch ? 'Best match' : 'Amount match'}
                    </span>
                  ) : null}
                </span>
              </InfoField>
            </div>
            <div className="col-6 col-md-4">
              <InfoField label={labelKey.expenseType || 'Expense Type'}>
                {d.expenseType?.title || d.expenseTypeTitle || '—'}
              </InfoField>
            </div>         
            <div className="col-6 col-md-4">
              <InfoField label={labelKey.paymentMethod || 'Payment Method'}>
                {d.paymentMethod?.title || '—'}
              </InfoField>
            </div>
  
            <div className="col-6 col-md-4">
              <InfoField label={labelKey.vendor || 'Vendor'}>{d.vendor?.name || '—'}</InfoField>
            </div>
            <div className="col-6 col-md-4">
              <InfoField label="Item Source">{renderSource(d.recordSourceId)}</InfoField>
            </div>
            {showRestrictedIds ? (
              <div className="col-6 col-md-4">
                <InfoField label="Line Item ID">{d.expenseItemId || '—'}</InfoField>
              </div>
            ) : null}
            {showRestrictedIds ? (
              <div className="col-6 col-md-4">
                <InfoField label="SS ReceiptID">{adminMeta.invoiceDataDraftId}</InfoField>
              </div>
            ) : null}
            <div className="col-6 col-md-4">
              <InfoField label="Item Created Date">
                {d.createdDate || d.createdDT ? (
                  <DateTimeComp formattedDate={d.createdDate || d.createdDT} />
                ) : (
                  '—'
                )}
              </InfoField>
            </div>
            <div className="col-6 col-md-4">
              <InfoField label="Receipt Uploaded Date">
                {d.receiptUploadedDateTime || d.receiptUploadedDate || d.invoiceDataDraft?.createdDT ? (
                  <DateTimeComp
                    formattedDate={
                      d.receiptUploadedDateTime || d.receiptUploadedDate || d.invoiceDataDraft?.createdDT
                    }
                  />
                ) : (
                  '—'
                )}
              </InfoField>
            </div>
            <div className="col-12">
              <InfoField label="Item Description">
                {d.itemDetail?.trim() ? d.itemDetail : '—'}
              </InfoField>
            </div>
            {showRestrictedIds ? (
              <div className="col-12">
                <InfoField label="Internal Notes">
                  {d.internalNotes?.trim() ? d.internalNotes : '—'}
                </InfoField>
              </div>
            ) : null}
          </div>
        </div>

        <div
          className={isPreview ? 'col-12 col-lg-2' : 'col-12 col-lg-4'}
          style={isPreview ? { flex: '0 0 30%', maxWidth: '30%' } : undefined}
        >
          {isPreview ? (
            <div className="text-muted text-uppercase fw-semibold mb-1" style={{ fontSize: 9 }}>
              Final attached receipt
            </div>
          ) : null}
          {hasReceipt ? (
            <div
              className="position-relative border rounded merge-panel-receipt w-100 overflow-hidden"
              style={{ height: isPreview ? 220 : 180 }}
            >
              <button
                type="button"
                className="btn btn-sm btn-icon btn-light-primary position-absolute top-0 end-0 m-1 shadow-sm"
                style={{ zIndex: 2, width: 28, height: 28 }}
                title="Open in new tab"
                aria-label="Open receipt in new tab"
                onClick={openReceiptInNewTab}
              >
                <FaExternalLinkAlt size={11} />
              </button>
              {isPdf ? (
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent w-100 h-100 d-flex flex-column align-items-center justify-content-center text-primary gap-1"
                  title="Open receipt in new tab"
                  onClick={openReceiptInNewTab}
                >
                  <FaFilePdf size={28} />
                  <span className="fw-semibold" style={{ fontSize: 11 }}>
                    PDF receipt
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn p-0 border-0 bg-transparent w-100 h-100"
                  title="Open receipt in new tab"
                  onClick={openReceiptInNewTab}
                >
                  <img
                    src={attachmentFile}
                    alt={`Receipt ${receiptNo}`}
                    className="w-100 h-100"
                    style={{ objectFit: 'contain', objectPosition: 'center top' }}
                  />
                </button>
              )}
            </div>
          ) : (
            <div
              className="border rounded merge-panel-receipt d-flex align-items-center justify-content-center text-muted"
              style={{ height: isPreview ? 220 : 180, fontSize: 11 }}
            >
              No receipt attached
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MergeSelectedLineItemHeader
